// Landing sections 4–13 (minus pricing): product intro, how-it-works,
// local-first, optimization capabilities, Energy Intelligence, integrations,
// tech specs, final CTA. Copy lives in i18n; structure here.
import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DEFAULT_REGION, money, region } from "../../config/finance";
import { Check } from "lucide-react";
import { BatteryCharging, Cable, CalendarClock, Cloud, CloudSun, Cpu, FileDown, FileText, Gauge, HardDrive, House, Lightbulb, ListChecks, Network, PiggyBank, Plug, RefreshCw, Rows3, Ruler, Settings2, ShieldCheck, Siren, Thermometer, ToggleRight, TrendingDown, TrendingUp, Wifi, Zap } from "../../components/icons";

// ---------------------------------------------------------------------------
// 4. Product introduction — image AFTER value story, two-column on desktop.
// ---------------------------------------------------------------------------
export const ProductIntro = forwardRef<HTMLElement>(function ProductIntro(_, ref) {
  const { t } = useTranslation();
  // No region selector on the landing page — quote the default region.
  const home = region(DEFAULT_REGION);
  const chips = [
    "din", "rs485", "psu", "relays", "api", "mqtt", "shelly", "cloud",
  ] as const;
  return (
    <section ref={ref} id="product" data-bg="hardware" className="border-y border-border bg-panel/75">
      <div className="max-w-6xl mx-auto px-4 py-14 grid gap-8 lg:grid-cols-2 items-center">
        <img
          src="/device.jpg"
          alt={t("landing.product.imageAlt")}
          className="rounded-xl border border-border shadow-card w-full h-auto"
          loading="lazy"
        />
        <div>
          <h2 className="text-2xl md:text-3xl font-semibold">{t("landing.product.title")}</h2>
          <p className="text-muted mt-3">{t("landing.product.sub")}</p>
          <ul className="mt-5 grid grid-cols-2 gap-2 text-sm">
            {chips.map((key) => (
              <li key={key} className="flex items-center gap-2">
                <Check size={14} className="text-good shrink-0" />
                {t(`landing.product.f_${key}`)}
              </li>
            ))}
          </ul>
          {/* Price next to the hardware itself, so the device introduction
              answers "what does it cost" without a scroll. Same figures as
              the pricing section — both read config/finance.json. */}
          <div className="mt-6 pt-5 border-t border-border flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <div className="text-2xl font-semibold font-mono">
                {money(home, home.hardwarePrice)}
                <span className="text-sm text-muted font-sans"> {t("landing.exVat")}</span>
              </div>
              <div className="text-xs text-muted mt-1">
                {t("landing.product.installLine", {
                  defaultValue: "{{from}} {{price}} {{vat}} standard installation",
                  from: t("landing.fromPrice"),
                  price: money(home, home.installationPrice),
                  vat: t("landing.exVat"),
                })}
              </div>
            </div>
            <a href="#pricing" className="btn-ghost">
              {t("landing.product.seePricing", { defaultValue: "See plans" })}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
});

// ---------------------------------------------------------------------------
// 5. How it works — Connect / Plan / Control / Explain.
// ---------------------------------------------------------------------------
export function HowItWorks() {
  const { t } = useTranslation();
  const steps = [
    { key: "connect", icon: <Cable size={20} /> },
    { key: "plan", icon: <CalendarClock size={20} /> },
    { key: "control", icon: <Zap size={20} /> },
    { key: "explain", icon: <FileText size={20} /> },
  ];
  return (
    <section id="how-it-works" className="max-w-6xl mx-auto px-4 py-14">
      <h2 className="text-2xl md:text-3xl font-semibold text-center">
        {t("landing.howSteps.title")}
      </h2>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((step, i) => (
          <div key={step.key} className="card p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-brand/10 text-brand">
                {step.icon}
              </span>
              <span className="text-xs font-mono text-muted">0{i + 1}</span>
            </div>
            <h3 className="font-semibold text-sm">{t(`landing.howSteps.${step.key}T`)}</h3>
            <p className="text-sm text-muted mt-1">{t(`landing.howSteps.${step.key}`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 6. Local-first positioning.
// ---------------------------------------------------------------------------
export function LocalFirst() {
  const { t } = useTranslation();
  const points = ["p1", "p2", "p3", "p4", "p5", "p6", "p7", "p8"] as const;
  return (
    <section className="border-y border-border bg-panel/75">
      <div className="max-w-6xl mx-auto px-4 py-14">
        <div className="flex items-center justify-center gap-2 text-good mb-2">
          <ShieldCheck size={20} />
        </div>
        <h2 className="text-2xl md:text-3xl font-semibold text-center">
          {t("landing.localFirstSec.title")}
        </h2>
        <p className="text-center text-muted mt-2 max-w-2xl mx-auto">
          {t("landing.localFirstSec.main")}
        </p>
        <ul className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-2 text-sm max-w-4xl mx-auto">
          {points.map((p) => (
            <li key={p} className="flex items-center gap-2">
              <Check size={14} className="text-good shrink-0" />
              {t(`landing.localFirstSec.${p}`)}
            </li>
          ))}
        </ul>
        <blockquote className="mt-8 max-w-3xl mx-auto card p-5 border-l-4 border-l-good text-sm">
          {t("landing.localFirstSec.statement")}
        </blockquote>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7. Optimization capabilities — outcome-phrased.
// ---------------------------------------------------------------------------
export function Capabilities() {
  const { t } = useTranslation();
  const caps = [
    { key: "prices", icon: <TrendingUp size={17} /> },
    { key: "schedule", icon: <CalendarClock size={17} /> },
    { key: "tariff", icon: <PiggyBank size={17} /> },
    { key: "peak", icon: <Gauge size={17} /> },
    { key: "pv", icon: <CloudSun size={17} /> },
    { key: "pva", icon: <ListChecks size={17} /> },
    { key: "replan", icon: <RefreshCw size={17} /> },
    { key: "incidents", icon: <Siren size={17} /> },
    { key: "recommend", icon: <Lightbulb size={17} /> },
  ];
  return (
    <section className="max-w-6xl mx-auto px-4 py-14">
      <h2 className="text-2xl md:text-3xl font-semibold text-center">
        {t("landing.caps.title")}
      </h2>
      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {caps.map((cap) => (
          <div key={cap.key} className="card p-4 flex items-start gap-3">
            <span className="text-brand mt-0.5 shrink-0">{cap.icon}</span>
            <p className="text-sm">{t(`landing.caps.${cap.key}`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 8. Energy Intelligence — structured report, never a chat box.
// ---------------------------------------------------------------------------
export function EnergyIntelligenceSection() {
  const { t } = useTranslation();
  const metrics = [
    { key: "daily", tone: "text-good" },
    { key: "arb", tone: "" },
    { key: "tariff", tone: "" },
    { key: "max", tone: "text-muted" },
    { key: "eff", tone: "text-good" },
    { key: "tomorrow", tone: "text-brand" },
  ] as const;
  return (
    <section className="border-y border-border bg-panel/75">
      <div className="max-w-6xl mx-auto px-4 py-14 grid gap-8 lg:grid-cols-2 items-start">
        <div>
          <div className="inline-flex items-center gap-2 text-accent text-sm font-medium">
            <FileText size={16} />
            {t("landing.value.intelligence.title")}
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold mt-2">{t("landing.ei.title")}</h2>
          <p className="text-muted mt-3">{t("landing.ei.sub")}</p>
          <ul className="mt-5 space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <TrendingDown size={15} className="mt-0.5 shrink-0" />
              {t("valuePages.intelligence.sample2")}
            </li>
            <li className="flex items-start gap-3">
              <Lightbulb size={15} className="mt-0.5 shrink-0" />
              {t("valuePages.intelligence.sample3")}
            </li>
          </ul>
          <Link to="/energy-intelligence" className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline mt-5">
            {t("landing.value.intelligence.cta")} →
          </Link>
        </div>
        <div className="card p-5">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-4">
            <FileText size={15} />
            {t("valuePages.intelligence.sampleTitle")}
          </h3>
          <dl className="space-y-2">
            {metrics.map((m) => (
              <div key={m.key} className="kv">
                <dt className="text-muted">{t(`landing.ei.m_${m.key}`)}</dt>
                <dd className={`v ${m.tone}`}>{t(`landing.ei.v_${m.key}`)}</dd>
              </div>
            ))}
          </dl>
          <p className="text-xs text-muted mt-4 border-t border-border pt-3">
            {t("valuePages.intelligence.disclaimer")}
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 9. Integrations — supporting benefits, plain chips (no third-party logos).
// ---------------------------------------------------------------------------
export function Integrations() {
  const { t } = useTranslation();
  // Same shape as the specs table: a name, one icon, and what you actually
  // get. Every value below describes a shipped capability — see
  // backend/app/shelly.py and shelly_mdns.py for the Home Assistant path.
  const rows = [
    { key: "homeAssistant", icon: <House size={15} /> },
    { key: "shelly", icon: <ToggleRight size={15} /> },
    { key: "restApi", icon: <Network size={15} /> },
    { key: "export", icon: <FileDown size={15} /> },
    { key: "profiles", icon: <HardDrive size={15} /> },
    { key: "remoteApi", icon: <Cloud size={15} /> },
  ] as const;
  return (
    <section id="integrations" className="max-w-3xl mx-auto px-4 py-12">
      <h2 className="text-xl font-semibold text-center flex items-center justify-center gap-2">
        <Network size={18} />
        {t("landing.integrations.title")}
      </h2>
      <div className="card mt-6 overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-border last:border-b-0">
                <th className="text-left font-normal text-muted px-4 py-2.5 w-2/5 align-top">
                  <span className="flex items-center gap-2">
                    <span className="text-muted shrink-0" aria-hidden="true">{row.icon}</span>
                    {t(`landing.integrations.${row.key}`)}
                  </span>
                </th>
                <td className="px-4 py-2.5">{t(`landing.integrations.${row.key}V`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted text-center mt-3">{t("landing.integrations.note")}</p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 12. Technical specifications — supports the decision, doesn't lead it.
// ---------------------------------------------------------------------------
export function TechSpecs() {
  const { t } = useTranslation();
  // One icon per spec, all in the same muted ink as the section heading —
  // they mark the rows without competing with the values for attention.
  const rows = [
    { key: "mounting", icon: <Rows3 size={15} /> },
    { key: "dimensions", icon: <Ruler size={15} /> },
    { key: "supply", icon: <Plug size={15} /> },
    { key: "relays", icon: <ToggleRight size={15} /> },
    { key: "comms", icon: <Cable size={15} /> },
    { key: "network", icon: <Wifi size={15} /> },
    { key: "protocols", icon: <Network size={15} /> },
    { key: "environment", icon: <Thermometer size={15} /> },
    { key: "inverters", icon: <Cpu size={15} /> },
  ] as const;
  return (
    <section id="tech-specs" className="max-w-3xl mx-auto px-4 py-14">
      <h2 className="text-xl font-semibold text-center flex items-center justify-center gap-2">
        <HardDrive size={18} />
        {t("landing.specs.title")}
      </h2>
      <div className="card mt-6 overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-border last:border-b-0">
                <th className="text-left font-normal text-muted px-4 py-2.5 w-2/5 align-top">
                  <span className="flex items-center gap-2">
                    <span className="text-muted shrink-0" aria-hidden="true">{row.icon}</span>
                    {t(`landing.specs.${row.key}`)}
                  </span>
                </th>
                <td className="px-4 py-2.5">{t(`landing.specs.${row.key}V`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted text-center mt-3">{t("landing.specs.note")}</p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 13. Final CTA.
// ---------------------------------------------------------------------------
export function FinalCta({ onCta }: { onCta: () => void }) {
  const { t } = useTranslation();
  return (
    <section className="max-w-3xl mx-auto px-4 py-16 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand/10 text-brand mb-4">
        <BatteryCharging size={22} />
      </div>
      <h2 className="text-2xl md:text-3xl font-semibold">{t("landing.finalCta.title")}</h2>
      <p className="text-muted mt-3">{t("landing.finalCta.sub")}</p>
      <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
        <Link to="/register" className="btn-primary text-base px-5 py-2.5" onClick={onCta}>
          {t("landing.ctaPrimary")}
        </Link>
        <a href="#pricing" className="btn-ghost text-base px-5 py-2.5">
          {t("landing.finalCta.pricing")}
        </a>
      </div>
      <p className="text-xs text-muted mt-4 inline-flex items-center gap-1.5">
        <Settings2 size={12} />
        {t("landing.pricingHardware")}
      </p>
    </section>
  );
}
